import {
  FC,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpIcon,
  ChevronUpIcon,
  ExclamationCircleIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { type CodyAsk, useTripContext } from '@/context/TripContext.tsx';
import { cn } from '@/utils/classNames.ts';
import truncate from '@/utils/truncate.ts';

const CODY_ASK_LABEL_MAX_LENGTH = 120;
const REQUEST_MAX_LENGTH = 500;
const COUNTER_VISIBLE_FROM = 400;
const COMPOSER_MAX_HEIGHT = 128;

const GUTTERS = 'px-5 md:px-8 lg:px-10 2xl:px-14';
const PRESS = 'transition-[transform,background-color,border-color,color] duration-150 ease-out active:scale-[0.97]';
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-journi-theme-light';

type CodyBarProps = {
  asks: CodyAsk[];
};

const CodyBar: FC<CodyBarProps> = ({ asks }) => {
  const { t } = useTranslation();
  const {
    isLoadingAction,
    handleCodyRefinement,
    codyRefinementError,
    failedCodyRequest,
    retryCodyRefinement,
    dismissCodyRefinementError,
    codyRefinementPhase,
    pendingPlacesCount,
  } = useTripContext();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const footerRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const composerId = useId();

  const isRefining = codyRefinementPhase === 'refining';
  const request = draft.trim();
  const canSend = request.length > 0 && !isLoadingAction;

  const groups = useMemo(
    () =>
      [
        {
          key: 'for_this_trip',
          title: t('tripPage.cody_group_for_this_trip'),
          asks: asks.filter((ask) => 'suggestionId' in ask),
          isTailored: true,
        },
        {
          key: 'anytime',
          title: t('tripPage.cody_group_anytime'),
          asks: asks.filter((ask) => !('suggestionId' in ask)),
          isTailored: false,
        },
      ].filter((group) => group.asks.length > 0),
    [asks, t]
  );

  const fitComposer = useCallback(() => {
    const composer = composerRef.current;
    if (!composer) return;

    composer.style.height = '0px';
    composer.style.height = `${Math.min(composer.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }, []);

  useLayoutEffect(fitComposer, [draft, fitComposer]);

  useEffect(() => {
    const composer = composerRef.current;
    if (!composer) return;

    composer.setAttribute('enterkeyhint', 'send');

    let lastWidth = composer.clientWidth;
    const observer = new ResizeObserver(() => {
      if (composer.clientWidth === lastWidth) return;

      lastWidth = composer.clientWidth;
      fitComposer();
    });

    observer.observe(composer);
    void document.fonts?.ready.then(fitComposer);

    return () => observer.disconnect();
  }, [fitComposer]);

  useEffect(() => {
    if (!isSheetOpen) return;

    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!footerRef.current?.contains(event.target as Node)) setIsSheetOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsidePress);

    return () => document.removeEventListener('pointerdown', closeOnOutsidePress);
  }, [isSheetOpen]);

  const pickAsk = (ask: CodyAsk) => {
    setIsSheetOpen(false);
    handleCodyRefinement(ask);
  };

  const send = (event?: FormEvent) => {
    event?.preventDefault();
    if (!canSend) return;

    setDraft('');
    setIsSheetOpen(false);
    handleCodyRefinement({ label: truncate(request, CODY_ASK_LABEL_MAX_LENGTH), updateGuidance: request });
  };

  const sendOnEnter = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;

    event.preventDefault();
    send();
  };

  const closeSheetOnEscape = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' || !isSheetOpen) return;

    setIsSheetOpen(false);
    toggleRef.current?.focus();
  };

  const editFailedRequest = () => {
    if (!failedCodyRequest) return;

    setDraft(failedCodyRequest.slice(0, REQUEST_MAX_LENGTH));
    dismissCodyRefinementError();
    composerRef.current?.focus();
  };

  return (
    <footer ref={footerRef} onKeyDown={closeSheetOnEscape} className="fixed inset-x-0 bottom-0 z-40">
      <div
        className={cn(
          'border-t border-primary/35 bg-journi-theme-light pb-3 pt-3 shadow-[0_-12px_32px_-16px_rgba(47,45,42,0.22)] md:pb-3.5',
          GUTTERS
        )}
      >
        {codyRefinementError ? (
          <div
            role="alert"
            className="mb-3 flex flex-col gap-3 rounded-2xl border border-error/25 bg-error/[0.06] px-4 py-3 sm:flex-row sm:items-center"
          >
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <ExclamationCircleIcon aria-hidden="true" className="mt-px h-5 w-5 shrink-0 text-error" />
              <p className="text-pretty text-sm leading-5 text-[#2F2D2A]">{codyRefinementError}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5 pl-8 sm:pl-0">
              <button
                type="button"
                disabled={isLoadingAction}
                onClick={retryCodyRefinement}
                className={cn(
                  'inline-flex min-h-9 items-center rounded-full border border-error/40 bg-white px-3.5 text-sm font-medium text-[#2F2D2A] disabled:opacity-50 [@media(hover:hover)]:hover:border-error/70',
                  PRESS,
                  FOCUS_RING
                )}
              >
                {t('button.try_again')}
              </button>
              {failedCodyRequest ? (
                <button
                  type="button"
                  disabled={isLoadingAction}
                  onClick={editFailedRequest}
                  className={cn(
                    'inline-flex min-h-9 items-center rounded-full px-3.5 text-sm font-medium text-[#2F2D2A] disabled:opacity-50 [@media(hover:hover)]:hover:bg-error/10',
                    PRESS,
                    FOCUS_RING
                  )}
                >
                  {t('button.edit_request')}
                </button>
              ) : null}
              <button
                type="button"
                disabled={isLoadingAction}
                onClick={dismissCodyRefinementError}
                aria-label={t('button.close')}
                className={cn(
                  'ml-auto inline-flex h-9 w-9 items-center justify-center rounded-full text-[#3F3D3A] disabled:opacity-50 sm:ml-0 [@media(hover:hover)]:hover:bg-error/10',
                  PRESS,
                  FOCUS_RING
                )}
              >
                <XMarkIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            </div>
          </div>
        ) : null}

        <div className="relative flex items-end gap-2 md:gap-3">
          <button
            ref={toggleRef}
            type="button"
            aria-expanded={isSheetOpen}
            aria-controls="cody-asks-panel"
            onClick={() => setIsSheetOpen((open) => !open)}
            className={cn(
              'inline-flex h-12 shrink-0 items-center gap-1.5 rounded-full border border-primary/45 bg-white pl-3.5 pr-3 text-sm font-medium text-[#2F2D2A] md:gap-2 md:pl-4 md:pr-3.5',
              '[@media(hover:hover)]:hover:border-primary aria-expanded:border-primary aria-expanded:bg-primary/15',
              PRESS,
              FOCUS_RING
            )}
          >
            <SparklesIcon aria-hidden="true" className="h-5 w-5 text-gold-deep" />
            <span className="sr-only">
              {isSheetOpen ? t('tripPage.cody_ideas_hide') : t('tripPage.cody_ideas_show')}
            </span>
            <span className="tabular-nums md:hidden">{asks.length}</span>
            <span className="hidden tabular-nums md:inline">
              {t('tripPage.cody_idea_count', { count: asks.length })}
            </span>
            <ChevronUpIcon
              aria-hidden="true"
              className={cn(
                'hidden h-4 w-4 text-[#3F3D3A] transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none md:block',
                isSheetOpen && 'rotate-180'
              )}
            />
          </button>

          <div
            id="cody-asks-panel"
            className={cn(
              'absolute bottom-[calc(100%+0.75rem)] left-0 right-0 origin-bottom-left md:right-auto md:w-[min(44rem,100%)]',
              'rounded-[1.75rem] border border-primary/30 bg-journi-theme-light shadow-[0_20px_48px_-20px_rgba(47,45,42,0.35),0_2px_6px_rgba(47,45,42,0.06)]',
              'transition-[opacity,transform,visibility] ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-[opacity,visibility]',
              isSheetOpen
                ? 'visible translate-y-0 scale-100 opacity-100 duration-200'
                : 'invisible translate-y-2 scale-[0.98] opacity-0 duration-150 motion-reduce:translate-y-0 motion-reduce:scale-100'
            )}
          >
            <div className="flex max-h-[min(60dvh,30rem)] flex-col gap-5 overflow-y-auto overscroll-contain px-5 py-5 [scrollbar-color:rgba(201,168,76,0.45)_transparent] [scrollbar-width:thin] md:px-6">
              {groups.map((group, index) => (
                <section
                  key={group.key}
                  aria-labelledby={`${composerId}-${group.key}`}
                  style={{ transitionDelay: isSheetOpen ? `${60 + index * 50}ms` : '0ms' }}
                  className={cn(
                    'flex flex-col gap-2.5 transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:translate-y-0 motion-reduce:transition-opacity',
                    isSheetOpen ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'
                  )}
                >
                  <h3
                    id={`${composerId}-${group.key}`}
                    className="font-sans text-[0.6875rem] font-medium uppercase tracking-[0.14em] text-text-secondary"
                  >
                    {group.title}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {group.asks.map((ask, askIndex) => (
                      <button
                        key={`${ask.label}-${askIndex}`}
                        type="button"
                        disabled={isLoadingAction}
                        onClick={() => pickAsk(ask)}
                        className={cn(
                          'inline-flex min-h-11 max-w-full items-start gap-2 rounded-3xl border bg-white px-3.5 py-3 text-left font-sans text-[0.9375rem] font-medium leading-5 text-[#2F2D2A] md:min-h-10 md:px-4 md:py-2.5 md:text-sm',
                          'disabled:cursor-not-allowed disabled:opacity-50',
                          group.isTailored
                            ? 'border-primary/55 [@media(hover:hover)]:hover:border-primary [@media(hover:hover)]:hover:bg-primary/10'
                            : 'border-primary/30 [@media(hover:hover)]:hover:border-primary/70 [@media(hover:hover)]:hover:bg-primary/10',
                          PRESS,
                          FOCUS_RING
                        )}
                      >
                        {group.isTailored ? (
                          <SparklesIcon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-gold-deep" />
                        ) : null}
                        <span className="min-w-0">{ask.label}</span>
                      </button>
                    ))}
                  </div>
                </section>
              ))}
              <p className="text-pretty font-sans text-xs leading-5 text-text-secondary md:hidden">
                {t('tripPage.refinement_beta_description')}
              </p>
            </div>
          </div>

          <form
            onSubmit={send}
            className={cn(
              'flex min-h-12 min-w-0 flex-1 items-end gap-2 rounded-3xl border border-primary/45 bg-white py-1.5 pl-4 pr-1.5 md:gap-3 md:pl-5',
              'shadow-[0_1px_2px_rgba(47,45,42,0.06)] transition-[border-color,box-shadow] duration-200 ease-out',
              'focus-within:border-primary focus-within:shadow-[0_0_0_4px_rgba(201,168,76,0.18)]'
            )}
          >
            <label
              htmlFor={composerId}
              className="sr-only shrink-0 cursor-text self-start font-feature text-xl font-light italic leading-9 text-[#2F2D2A] sm:not-sr-only"
            >
              {t('tripPage.ask_cody_to')}
            </label>
            <div className="relative min-w-0 flex-1">
              {draft.length === 0 ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'pointer-events-none absolute inset-x-0 top-0 block truncate font-sans text-base leading-9 text-[#2F2D2A]/45',
                    isLoadingAction && 'opacity-60'
                  )}
                >
                  <span className="font-feature text-xl font-light italic text-[#2F2D2A] sm:hidden">
                    {t('tripPage.ask_cody_to')}{' '}
                  </span>
                  {t('placeholder.cody_change_request')}
                </span>
              ) : null}
              <textarea
                ref={composerRef}
                id={composerId}
                rows={1}
                value={draft}
                maxLength={REQUEST_MAX_LENGTH}
                disabled={isLoadingAction}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={sendOnEnter}
                className="block min-h-9 w-full resize-none bg-transparent py-1.5 font-sans text-base leading-6 text-[#2F2D2A] caret-[var(--gold-deep)] outline-none selection:bg-primary/30 disabled:cursor-not-allowed disabled:opacity-60"
              />
            </div>
            {request.length >= COUNTER_VISIBLE_FROM ? (
              <span className="shrink-0 self-center font-sans text-xs tabular-nums text-text-secondary">
                {request.length}/{REQUEST_MAX_LENGTH}
              </span>
            ) : null}
            <button
              type="submit"
              disabled={!canSend}
              aria-label={t('codyPopup.submit')}
              className={cn(
                'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-ink',
                'disabled:cursor-not-allowed disabled:bg-primary/20 disabled:text-[#0A0A0A]/35 [@media(hover:hover)]:enabled:hover:bg-gold-deep',
                isRefining && 'disabled:bg-primary/30',
                PRESS,
                FOCUS_RING
              )}
            >
              {isRefining ? (
                <span aria-hidden="true" className="loading loading-spinner loading-xs text-[#0A0A0A]/70" />
              ) : (
                <ArrowUpIcon aria-hidden="true" className="h-[18px] w-[18px] stroke-2" />
              )}
            </button>
          </form>
        </div>

        <div className="mt-2.5 flex min-h-5 flex-wrap items-center gap-x-3 gap-y-1 font-sans text-[0.6875rem] leading-5 text-text-secondary sm:text-xs">
          {codyRefinementPhase === 'syncing' && pendingPlacesCount > 0 ? (
            <p role="status" aria-live="polite" className="inline-flex items-center gap-2 text-[#3F3D3A]">
              <span aria-hidden="true" className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60 motion-reduce:animate-none" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
              </span>
              {t('tripPage.mapping_new_stops', { count: pendingPlacesCount })}
            </p>
          ) : (
            <>
              <span className="inline-flex h-5 items-center rounded-full border border-primary/60 px-2 text-[0.625rem] font-medium uppercase tracking-[0.14em] text-[#2F2D2A] md:hidden">
                {t('tripPage.refinement_beta_label')}
              </span>
              <p className="hidden md:block">{t('tripPage.refinement_beta_description')}</p>
            </>
          )}
          <p className="ml-auto">
            <span className="hidden sm:inline">{t('tripPage.attribution.venue_details')} </span>
            <a
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noreferrer"
              className="underline decoration-primary/50 underline-offset-2 [@media(hover:hover)]:hover:decoration-primary"
            >
              {t('tripPage.attribution.openstreetmap')}
            </a>
            {' · '}
            <a
              href="https://www.geoapify.com/"
              target="_blank"
              rel="noreferrer"
              className="underline decoration-primary/50 underline-offset-2 [@media(hover:hover)]:hover:decoration-primary"
            >
              {t('tripPage.attribution.geoapify')}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default CodyBar;
